import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { useServerFn } from "@tanstack/react-start";
import { listGateProgress, type GateProgressRow } from "@/lib/gates.functions";
import { GATES } from "@/lib/gates";
import { GateProgressMap } from "@/components/site/GateProgressMap";
import { Card } from "@/components/ui/card";
import { RecommendationsRail } from "@/components/site/RecommendationsRail";
import { Button } from "@/components/ui/button";
import { List, Sparkles } from "lucide-react";
import { haptic } from "@/lib/haptics";
import { getTodaysActivation, type TodayActivation as TodayRow } from "@/lib/activations-today.functions";

export const Route = createFileRoute("/_authenticated/journey/map")({
  component: MapPage,
});

function MapPage() {
  const load = useServerFn(listGateProgress);
  const loadToday = useServerFn(getTodaysActivation);
  const [progress, setProgress] = useState<GateProgressRow[] | null>(null);
  const [today, setToday] = useState<TodayRow | null>(null);
  const [selectedGate, setSelectedGate] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelTitleId = useId();
  const panelDescId = useId();

  const [listView, setListView] = useState(
    typeof window !== "undefined" && window.matchMedia?.("(max-width: 640px)").matches,
  );
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    load().then(setProgress).catch(() => setProgress([]));
    loadToday().then((r) => setToday(r as TodayRow | null)).catch(() => setToday(null));
  }, [load, loadToday]);

  // Move focus into the preview panel when it opens.
  useEffect(() => {
    if (selectedGate && panelRef.current) {
      panelRef.current.focus();
    }
  }, [selectedGate]);

  // Esc closes the preview and returns focus to the originating node.
  useEffect(() => {
    if (!selectedGate) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeSelection();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGate]);

  const closeSelection = () => {
    const n = selectedGate;
    setSelectedGate(null);
    // Restore focus to the corresponding node after render.
    requestAnimationFrame(() => {
      if (n == null) return;
      const node = document.querySelector<SVGGElement>(`[data-gate="${n}"]`);
      node?.focus();
    });
  };


  const byGate = useMemo(() => {
    const m = new Map<number, GateProgressRow>();
    for (const p of progress ?? []) m.set(p.gate_n, p);
    return m;
  }, [progress]);

  const currentN = useMemo(() => {
    if (!progress) return 1;
    const completed = new Set(progress.filter((p) => p.completed_at).map((p) => p.gate_n));
    const inProgress = progress.find((p) => !p.completed_at && (p.actions?.length ?? 0) > 0);
    if (inProgress) return inProgress.gate_n;
    const highest = completed.size ? Math.max(...Array.from(completed)) : 0;
    return Math.min(47, highest + 1);
  }, [progress]);

  if (!progress) {
    return <div className="rounded-md border hairline p-6 text-sm text-foreground/60">Loading your map…</div>;
  }

  const completedCount = progress.filter((p) => p.completed_at).length;

  return (
    <div className="space-y-4">
      {today?.related_gate_n && (
        <Card className="flex items-center justify-between gap-3 p-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Today points to</p>
            <p className="mt-1 truncate font-serif text-lg text-gold-luminous">
              Gate {today.related_gate_n} · {GATES.find((g) => g.n === today.related_gate_n)?.title}
            </p>
            <p className="mt-1 truncate text-xs text-foreground/60">via "{today.title}"</p>
          </div>
          <Link
            to="/gates/$n"
            params={{ n: String(today.related_gate_n) }}
            className="shrink-0 rounded-full border hairline px-3 py-1.5 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold"
          >
            Open →
          </Link>
        </Card>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-foreground/70">
          {completedCount === 0
            ? "Begin at the First Gate. Each Gate you walk illuminates the path."
            : `${completedCount} of 47 walked. Your constellation grows as you return.`}
        </p>
        <Button variant="outline" size="sm" onClick={() => setListView((v) => !v)}>
          <List size={14} className="mr-1" />
          {listView ? "Constellation" : "List view"}
        </Button>
      </div>

      {listView ? (
        <Card className="p-4">
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {GATES.map((g) => {
              const row = byGate.get(g.n);
              const state: "current" | "completed" | "future" = row?.completed_at
                ? "completed"
                : g.n === currentN
                  ? "current"
                  : "future";
              return (
                <li key={g.n}>
                  <Link
                    to="/gates/$n"
                    params={{ n: String(g.n) }}
                    onClick={() =>
                      haptic(state === "current" ? "medium" : "light", `journey_map_list:${state}`)
                    }
                    className={`flex items-center justify-between rounded-md border p-3 hover:border-gold ${
                      state === "current"
                        ? "border-gold bg-gold/5"
                        : state === "completed"
                          ? "border-gold/60"
                          : "hairline"
                    }`}
                  >
                    <span className="text-sm">
                      Gate {g.n} · {g.title}
                    </span>
                    <span className="text-xs text-foreground/50">{state}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <GateProgressMap
            progress={progress}
            reduceMotion={reduceMotion}
            selectedGate={selectedGate}
            onSelect={(n) => {
              haptic("light", "journey_map_node");
              setSelectedGate(n);
            }}
          />
        </Card>
      )}

      {selectedGate && !listView && (() => {
        const g = GATES.find((x) => x.n === selectedGate);
        if (!g) return null;
        const row = byGate.get(g.n);
        const status: "walked" | "current" | "ahead" = row?.completed_at
          ? "walked"
          : g.n === currentN
            ? "current"
            : "ahead";
        const theme = g.title.replace(/^The Gate of\s+/i, "");
        const difficulty = g.n <= 15 ? "Foundation" : g.n <= 31 ? "Deepening" : "Mastery";
        const teaser = (g.reflection.match(/^[^.!?]+[.!?]/)?.[0] ?? g.reflection).trim();
        return (
          <Card
            ref={panelRef as unknown as React.Ref<HTMLDivElement>}
            role="region"
            aria-labelledby={panelTitleId}
            aria-describedby={panelDescId}
            tabIndex={-1}
            className="p-4 border-gold/40 bg-gold/[0.03] transition-shadow focus:outline-none focus-visible:border-gold focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian focus-visible:shadow-[0_0_0_1px_rgba(245,215,110,0.6),0_10px_30px_-10px_rgba(245,215,110,0.35)]"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  Gate {g.n} · {status}
                </p>
                <p id={panelTitleId} className="mt-1 font-serif text-lg text-gold-luminous">
                  {g.title}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className="rounded-full border hairline px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-foreground/70">
                    Theme · {theme}
                  </span>
                  <span className="rounded-full border hairline px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-foreground/70">
                    {difficulty}
                  </span>
                </div>
                <p id={panelDescId} className="mt-3 text-sm text-foreground/75">
                  {teaser}
                </p>
              </div>
              <button
                type="button"
                onClick={closeSelection}
                aria-label={`Close preview for Gate ${g.n}`}
                className="shrink-0 rounded-full border hairline px-2 py-1 text-xs text-foreground/60 hover:border-gold hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian"
              >
                <span aria-hidden="true">✕</span>
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/gates/$n"
                params={{ n: String(g.n) }}
                onClick={() => haptic("medium", "journey_map_enter")}
                aria-label={`Enter Gate ${g.n}: ${g.title}`}
                className="rounded-full border border-gold px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold hover:bg-gold/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian"
              >
                Enter Gate →
              </Link>
              <button
                type="button"
                onClick={closeSelection}
                className="rounded-full border hairline px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-foreground/70 hover:border-gold hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian"
              >
                Stay on map
              </button>
            </div>
          </Card>
        );
      })()}

      {/* Screen-reader announcement for node selection */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {selectedGate
          ? (() => {
              const g = GATES.find((x) => x.n === selectedGate);
              if (!g) return "";
              const row = byGate.get(g.n);
              const status = row?.completed_at ? "walked" : g.n === currentN ? "current" : "ahead";
              return `Previewing Gate ${g.n}, ${g.title}, ${status}. Press Enter Gate to open, or Escape to close.`;
            })()
          : ""}
      </div>




      <div className="grid gap-3 sm:grid-cols-3 text-xs text-foreground/70">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-gold" /> Walked
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#fff3c4]" /> Current gate
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-gold/30" /> Ahead
        </div>
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-2 text-sm">
          <Sparkles size={14} className="text-gold" />
          <span>
            As clusters of Gates complete, the{" "}
            <Link to="/journey/temple" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
              Temple of Consciousness
            </Link>{" "}
            grows around you.
          </span>
        </div>
      </Card>
      <RecommendationsRail limit={4} heading="Suggested next direction" />
    </div>
  );
}
